/**
 * AI bill explainer — C3.
 * "Watch the AI think" → live /api/ai-explainer fetch → typewriter reveal.
 * Citation badges map to real IntentCitation records from the server registry.
 * 503 / network failure → FALLBACK_UNAVAILABLE canned response + banner (ADR-014).
 * All 5 personas now receive live AI (Pages Function handles P1–P5).
 * Atlas: C3.
 */
"use client";

import Link from "next/link";
import { ChevronLeft, Sparkles, Info, MessageCircle, HelpCircle, AlertCircle } from "lucide-react";
import { Tooltip } from "@/components/Tooltip";
import { CitationText } from "@/components/CitationText";
import { useState, useEffect, useCallback, useRef } from "react";
import { FALLBACK_UNAVAILABLE } from "@petci/fixtures";
import { useActivePersona } from "@/lib/persona";
import { useT, useLocaleTag, useLocaleStore } from "@/lib/locale";
import { formatMonth } from "@/lib/format";
import { apiClient } from "@/lib/api-client";
import { useReducedMotion } from "@petci/shared-ui";
import { useTypewriter } from "@/lib/use-typewriter";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Unified citation shape — matches both CannedCitation and IntentCitation. */
interface LiveCitation { badgeLabel: string; detail: string }

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// 12ms/char = ~83 chars/sec theoretical. AssistantPanel (10ms/char, ~100 chars/sec)
// verified at ~104 chars/sec on real foreground browser 2026-05-07 — well above atlas C3
// spec of ≥30 chars/sec. Do not change without re-verifying against the spec.
const TYPING_SPEED_MS = 12;
const INTRO_DELAY_MS = 400;

const THINK_STEPS = [
  "Querying your usage data…",
  "Cross-referencing fuel-factor history…",
  "Drafting explanation…",
] as const;
const THINK_STEP_MS = 500;

const FOLLOW_UP_CHIPS = [
  "What's a fuel factor?",
  "How can I reduce my bill?",
  "Compare to this time last year",
] as const;

// Generic per-persona fallback when API unavailable and no canned response exists.
function buildGenericFallback(
  totalStr: string,
  kwhStr: string,
  accountNumber: string,
): { text: string; citations: LiveCitation[] } {
  return {
    text: `Your current bill is $${totalStr} for ${kwhStr} kWh on account ${accountNumber}. [fuel factor]\n\nLive AI explanation is temporarily unavailable. Your standard rate includes the base electric charge plus a monthly fuel factor adjustment. [data verified]`,
    citations: [
      {
        badgeLabel: "fuel factor",
        detail: `Standard explanation — fuel factor applies to account ${accountNumber}. Source: current bill record.`,
      },
      {
        badgeLabel: "data verified",
        detail:
          "Response is being verified against account records. Standard explanation shown while live data is retrieved.",
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BillExplainPage() {
  const persona = useActivePersona();
  const t = useT();
  const tag = useLocaleTag();
  const reduceMotion = useReducedMotion();

  // Display state
  const [activeCitations, setActiveCitations] = useState<LiveCitation[]>([]);
  const [expandedBadge, setExpandedBadge] = useState<string | null>(null);
  const [thinkCompleted, setThinkCompleted] = useState<string[]>([]);
  const [thinkActive, setThinkActive] = useState<string | null>(null);
  const [showFallbackBanner, setShowFallbackBanner] = useState(false);

  // Coordination refs — avoids stale closures between async API and animation timers
  const pendingAiRef = useRef<{ text: string; citations: LiveCitation[] } | null>(null);
  const animDoneRef = useRef(false);

  // Text reveal owned by the shared typewriter primitive (rAF + hidden-tab
  // safety net) — see lib/use-typewriter.ts. This surface auto-asks on mount, so
  // on a hidden/headless tab the old rAF-only loop stranded the answer (F-A).
  const { displayedText, isDone, start: startTypewriter, clear: clearTypewriter } = useTypewriter(TYPING_SPEED_MS);
  const isTyping = displayedText.length > 0 && !isDone;

  // Hand a resolved answer to the typewriter; citations render alongside.
  const deliver = useCallback((text: string, citations: LiveCitation[]) => {
    setActiveCitations(citations);
    setExpandedBadge(null);
    startTypewriter(text);
  }, [startTypewriter]);

  // On persona change: fetch AI + run thinking animation concurrently.
  // Typewriter starts when BOTH the animation AND the API fetch are done.
  useEffect(() => {
    // Reset display
    clearTypewriter();
    setActiveCitations([]);
    setExpandedBadge(null);
    setThinkCompleted([]);
    setThinkActive(null);
    setShowFallbackBanner(false);
    pendingAiRef.current = null;
    animDoneRef.current = false;

    let cancelled = false;
    const allTimers: ReturnType<typeof setTimeout>[] = [];

    // Called when the thinking animation completes. If the API already responded,
    // start the typewriter immediately; otherwise wait for the API to call deliver.
    function onAnimDone() {
      if (cancelled) return;
      animDoneRef.current = true;
      const pending = pendingAiRef.current;
      if (pending) {
        deliver(pending.text, pending.citations);
      }
    }

    if (reduceMotion) {
      onAnimDone();
    } else {
      THINK_STEPS.forEach((step, idx) => {
        const t1 = setTimeout(() => {
          if (!cancelled) setThinkActive(step);
        }, idx * THINK_STEP_MS);
        const t2 = setTimeout(() => {
          if (cancelled) return;
          setThinkCompleted((prev) => [...prev, step]);
          if (idx === THINK_STEPS.length - 1) {
            setThinkActive(null);
            const t3 = setTimeout(onAnimDone, INTRO_DELAY_MS);
            allTimers.push(t3);
          }
        }, (idx + 1) * THINK_STEP_MS);
        allTimers.push(t1, t2);
      });
    }

    // Fetch live AI explanation — AbortController cancels on effect cleanup.
    const ac = new AbortController();
    let resultHandled = false;

    // Locale read imperatively (D-1), not as a reactive dependency: this effect
    // owns a ONE-SHOT ask-on-mount, so subscribing it to the locale would re-fire
    // a live AI call on every language toggle. Captured here — before any await
    // in the timer chain below — per the E4 stale-closure rule.
    const requestLocale = useLocaleStore.getState().locale;

    function getFallback(): { text: string; citations: LiveCitation[] } {
      if (persona.id === "P1") {
        return {
          text: FALLBACK_UNAVAILABLE.text,
          citations: FALLBACK_UNAVAILABLE.citations.map((c) => ({
            badgeLabel: c.badgeLabel,
            detail: c.detail,
          })),
        };
      }
      return buildGenericFallback(
        persona.currentBill.total.toFixed(2),
        String(persona.currentBill.usageKWh),
        persona.accountNumber,
      );
    }

    function handleAiResult(text: string, cits: LiveCitation[], isFallback: boolean) {
      if (cancelled || resultHandled) return;
      resultHandled = true;
      if (isFallback) setShowFallbackBanner(true);
      if (animDoneRef.current) {
        deliver(text, cits);
      } else {
        pendingAiRef.current = { text, citations: cits };
      }
    }

    // 8s hard timeout — show canned fallback rather than hang indefinitely
    const fetchTimeout = setTimeout(() => {
      const fb = getFallback();
      handleAiResult(fb.text, fb.citations, true);
    }, 8000);
    allTimers.push(fetchTimeout);

    async function fetchAi() {
      try {
        const result = await apiClient.explainBill(
          { intent: "bill-explainer", personaId: persona.id, locale: requestLocale },
          ac.signal,
        );

        if (!result.ok && result.error === "aborted") return;

        if (result.ok && !result.data.fallback && result.data.text && result.data.citations?.length) {
          handleAiResult(result.data.text, result.data.citations, false);
        } else {
          const fb = getFallback();
          handleAiResult(fb.text, fb.citations, true);
        }
      } catch {
        const fb = getFallback();
        handleAiResult(fb.text, fb.citations, true);
      }
    }

    void fetchAi();

    return () => {
      cancelled = true;
      ac.abort();
      allTimers.forEach((t) => clearTimeout(t));
    };
  }, [persona, persona.id, deliver, clearTypewriter]);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Link
        href="/bill"
        className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-900 transition-colors duration-fast py-1 -my-1"
      >
        <ChevronLeft size={16} aria-hidden />
        {t("bill.currentBillLink")}
      </Link>

      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
          <Sparkles size={20} className="text-brand-primary-text" aria-hidden />
        </span>
        <div>
          <h1 className="text-xl font-display font-bold text-neutral-900">{t("bill.understandTitle")}</h1>
          <p className="text-xs text-neutral-500 flex items-center gap-1">
            {t("bill.explainAccountLine", {
              month: formatMonth(persona.currentBill.month, tag),
              account: persona.accountNumber,
            })}
            <InfoPopover tip={t("bill.explainGroundedTip")} />
          </p>
        </div>
      </div>

      {/* Fallback banner — shown when AI is unavailable */}
      {showFallbackBanner && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-xl border border-semantic-warning/30 bg-semantic-warning/10 px-4 py-2.5 text-sm text-semantic-warning-text"
        >
          <AlertCircle size={15} aria-hidden className="flex-shrink-0" />
          {t("ai.standardExplanation")}
        </div>
      )}

      {/* Typing area */}
      <div
        className="card-surface space-y-4 min-h-48"
        aria-live="polite"
        aria-label="AI explanation"
        // Locale-independent state marker for the runtime audits. This route
        // auto-asks on mount, so a crawler that probes on `load` measures the
        // think animation and never sees the answer, the citation badges or the
        // disclaimer — `audit:e6` had been reporting green over all of it.
        // "settled" means the terminal chrome is rendered, and it is reached on
        // BOTH the live and the fallback path, so the wait resolves with or
        // without an AI backend.
        data-ai-state={isDone ? "settled" : "pending"}
      >
        {/* Thinking steps — "Watch the AI think" sequence */}
        {(thinkActive !== null || (thinkCompleted.length > 0 && !displayedText)) && (
          <div className="space-y-2">
            {THINK_STEPS.map((step) => {
              const complete = thinkCompleted.includes(step);
              const active = thinkActive === step;
              return (
                <div key={step} className="flex items-center gap-2 text-sm">
                  {complete ? (
                    <span className="text-semantic-success-text font-bold">✓</span>
                  ) : active ? (
                    <span
                      className="w-4 h-4 rounded-full border-2 border-brand-primary border-t-transparent animate-spin flex-shrink-0"
                      role="status"
                      aria-label="Processing"
                    />
                  ) : (
                    <span className="w-4 h-4 flex-shrink-0" />
                  )}
                  <span
                    className={
                      complete
                        ? "text-neutral-500"
                        : active
                          ? "text-neutral-700 font-medium"
                          : "text-neutral-500"
                    }
                  >
                    {step}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Loading pulse when animation complete but API still in flight */}
        {!displayedText && animDoneRef.current && !pendingAiRef.current && (
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <span className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-brand-primary animate-bounce"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </span>
            {t("bill.generatingExplanation")}
          </div>
        )}

        {/* Rendered text with inline citation badge rendering */}
        {displayedText && (
          <div className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">
            <TextWithBadges
              text={displayedText}
              citations={activeCitations}
              expandedBadge={expandedBadge}
              onBadgeClick={(label) =>
                setExpandedBadge(expandedBadge === label ? null : label)
              }
            />
            {isTyping && (
              <span className="inline-block w-0.5 h-4 bg-brand-primary animate-pulse ml-0.5 align-middle" />
            )}
          </div>
        )}

        {/* Citation detail panel */}
        {isDone && expandedBadge && (
          <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-xl p-3 text-xs text-neutral-700 space-y-1">
            <div className="flex items-center gap-1.5 font-semibold text-brand-primary-text">
              <Info size={12} aria-hidden />
              {t("bill.sourceLabel", { badge: expandedBadge })}
            </div>
            <p>
              {activeCitations.find((c) => c.badgeLabel === expandedBadge)?.detail}
            </p>
          </div>
        )}

        {/* Disclaimer */}
        {isDone && (
          <p className="text-xs text-neutral-500 italic border-t border-neutral-200 pt-3">
            {t("bill.aiDisclaimer")}
          </p>
        )}

        {/* CORE/UORE policy disclosure links */}
        {isDone && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 border-t border-neutral-200 pt-2">
            <span>{t("bill.tariffRefCore")}</span>
            <span>{t("bill.tariffRefUore")}</span>
          </div>
        )}
      </div>

      {/* Follow-up chips — navigate to assistant for H1 (wired in S3) */}
      {isDone && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
            {t("bill.askFollowUp")}
          </p>
          <div className="flex flex-wrap gap-2">
            {FOLLOW_UP_CHIPS.map((chip) => (
              <Link
                key={chip}
                href={`/support?prompt=${encodeURIComponent(chip)}`}
                className="text-sm px-3 py-1.5 rounded-full border border-brand-primary/30 text-brand-primary-text hover:bg-brand-primary/5 transition-colors duration-fast"
              >
                {chip}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Open full AI assistant CTA */}
      {isDone && (
        <div className="card-muted border-dashed flex items-center gap-4">
          <span className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
            <MessageCircle size={18} className="text-brand-primary-text" aria-hidden />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-neutral-800">{t("bill.askAnythingTitle")}</p>
            <p className="text-xs text-neutral-500 mt-0.5">
              {t("bill.askAnythingSub")}
            </p>
          </div>
          <Link
            href="/support"
            className="text-xs font-medium text-brand-primary-text hover:underline flex-shrink-0 py-1 -my-1"
          >
            {t("bill.openAssistant")}
          </Link>
        </div>
      )}

    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function InfoPopover({ tip }: { tip: string }) {
  return (
    <Tooltip width="w-64" content={<span className="leading-relaxed">{tip}</span>}>
      <button
        type="button"
        aria-label="More information"
        /* R-A: a 12x12 target — the smallest on any route. The O-P3-1 parent
           check says this is a real defect, not a child of a bigger target:
           `Tooltip` is a hover/focus wrapper, so this button IS the control.
           `p-1.5` takes it to 24x24; only the vertical margin is handed back,
           because the icon is last in its text line and the ~12px of extra
           width pushes nothing. */
        className="inline-flex items-center justify-center p-1.5 -my-1.5 text-neutral-500 hover:text-brand-primary-text focus:outline-none focus:text-brand-primary-text transition-colors duration-fast"
      >
        <HelpCircle size={12} aria-hidden />
      </button>
    </Tooltip>
  );
}

const TextWithBadges = CitationText;
